/**
 * Block ID Margin Component
 * 
 * Displays toggleable block IDs in the document editor margin.
 * Allows users to easily reference specific blocks in chat.
 */

import React, { useState, useCallback } from 'react';
import { Button } from '@/components/ui';

// Inline SVG icons to avoid external dependency
const HashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const CopyIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3 h-3 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

interface Block {
  id: number;
  type: 'heading' | 'paragraph' | 'list' | 'table' | 'code';
  content: string;
  level?: number;  // For headings
}

interface BlockIdMarginProps {
  blocks: Block[];
  showBlockIds: boolean;
  onToggleBlockIds: () => void;
  onBlockClick?: (blockId: number) => void;
  selectedBlockId?: number;
}

/**
 * Single block ID indicator
 */
function BlockIdIndicator({
  block,
  isSelected,
  onClick,
}: {
  block: Block;
  isSelected: boolean;
  onClick?: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`@${block.id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [block.id]);

  return (
    <div
      className={`group flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono cursor-pointer transition-all ${
        isSelected
          ? 'bg-info-light text-info'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
      onClick={onClick}
    >
      <span className="text-muted-foreground">§</span>
      <span>{block.id}</span>
      
      {/* Copy button - shown on hover */}
      <Button
        onClick={handleCopy}
        variant="ghost"
        size="icon"
        className="opacity-0 group-hover:opacity-100 ml-1 p-0.5 w-5 h-5"
        title="Copy block reference"
      >
        {copied ? (
          <CheckIcon />
        ) : (
          <CopyIcon />
        )}
      </Button>
    </div>
  );
}

/**
 * Toggle button for showing/hiding block IDs
 */
export function BlockIdToggle({
  showBlockIds,
  onToggle,
}: {
  showBlockIds: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      onClick={onToggle}
      variant={showBlockIds ? 'info' : 'secondary'}
      size="sm"
      className="flex items-center gap-2"
      title={showBlockIds ? 'Hide block IDs' : 'Show block IDs'}
    >
      <HashIcon />
      <span>Block IDs</span>
      {showBlockIds ? (
        <EyeIcon />
      ) : (
        <EyeOffIcon />
      )}
    </Button>
  );
}

/**
 * Block ID Margin - renders alongside document content
 */
export function BlockIdMargin({
  blocks,
  showBlockIds,
  onToggleBlockIds: _onToggleBlockIds,
  onBlockClick,
  selectedBlockId,
}: BlockIdMarginProps) {
  // _onToggleBlockIds is available for future use
  void _onToggleBlockIds;
  if (!showBlockIds) return null;

  return (
    <div className="flex flex-col gap-1 pr-2 border-r border-border select-none">
      {blocks.map(block => (
        <BlockIdIndicator
          key={block.id}
          block={block}
          isSelected={selectedBlockId === block.id}
          onClick={() => onBlockClick?.(block.id)}
        />
      ))}
    </div>
  );
}

/**
 * Inline block ID badge - for use within document content
 */
export function InlineBlockId({
  blockId,
  onClick,
}: {
  blockId: number;
  onClick?: () => void;
}) {
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-xs font-mono bg-muted text-muted-foreground cursor-pointer hover:bg-primary/90 dark:hover:bg-primary/90/30 hover:text-link-hover dark:hover:text-link-hover transition-colors"
      onClick={onClick}
    >
      <span className="text-muted-foreground">§</span>
      {blockId}
    </span>
  );
}

export default BlockIdMargin;

